/**
 * ChartBlock — the shared chart wrapper of the mockup: a legend row with a "טבלה" toggle, the
 * plot itself (LTR drawing area inside an RTL page) and the accessible table twin.
 *
 * Rules (spec-business §6 / §8): `role="group"` + a verbal `aria-label` summary on the figure,
 * a legend whenever there are ≥ 2 series, one tooltip per chart shown on hover AND on keyboard
 * focus, and a real `<table>` alternative for every chart.
 */

import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../components/Icon.tsx';
import { he } from '../i18n/he.ts';

export interface LegendItem {
  label: string;
  /** CSS colour, usually `var(--series-N)`. */
  color: string;
  kind?: 'square' | 'line' | 'dash';
}

export interface TableTwin {
  head: string[];
  rows: ReactNode[][];
  caption: string;
}

export function Legend({ items, twoCol }: { items: LegendItem[]; twoCol?: boolean }) {
  return (
    <div className={`legend${twoCol ? ' two-col' : ''}`} aria-label={he.app.legend}>
      {items.map((item) => (
        <span className="lg" key={item.label}>
          <i
            className={`sw${item.kind === 'line' ? ' line' : item.kind === 'dash' ? ' dash' : ''}`}
            style={item.kind === 'dash' ? { color: item.color } : { background: item.color }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export function ChartBlock({
  summary,
  legend,
  legendTwoCol,
  table,
  children,
  footer,
}: {
  /** One-sentence verbal takeaway — the figure's accessible name (spec §8 "תרשימים"). */
  summary: string;
  legend?: LegendItem[];
  legendTwoCol?: boolean;
  table: TableTwin;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);
  const id = useId();
  return (
    <div className="chart-block">
      <div className="chart-tools">
        {legend && legend.length ? <Legend items={legend} twoCol={legendTwoCol} /> : <span />}
        <button type="button" className="btn xs ghost" aria-pressed={showTable} aria-controls={id} onClick={() => setShowTable((v) => !v)}>
          <Icon name="table" className="sm" />
          {showTable ? he.app.chartView : he.app.tableView}
        </button>
      </div>
      <div id={id} className="chart-viz" role="group" aria-label={summary} hidden={showTable}>
        {children}
      </div>
      <div className="chart-table" hidden={!showTable}>
        <table>
          <caption className="sr">{table.caption}</caption>
          <thead>
            <tr>
              {table.head.map((cell, i) => (
                <th key={cell} scope="col" className={i ? 'n' : undefined}>
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className={ci ? 'n' : undefined}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}
